/**
 * @fileoverview Modal "Ver uso" do card OpenAI em /integrations.
 * Mostra 3 cards de resumo (custo/tokens/requests + delta vs período anterior),
 * gráfico de área diário, breakdowns por feature e por modelo, e tabela das
 * últimas chamadas. Padrão visual herdado do tagModalDialog moderno usado em
 * Integrations/index.js (radius 24, gradient header).
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  IconButton,
  Typography,
  CircularProgress,
  Tooltip as MuiTooltip,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  X,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Sparkles,
  RefreshCw,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { format, parseISO, startOfMonth, endOfMonth, subMonths, addDays } from "date-fns";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const PERIOD_PRESETS = [
  { id: "currentMonth", label: "Este mês" },
  { id: "last7", label: "Últimos 7 dias" },
  { id: "last30", label: "Últimos 30 dias" },
  { id: "previousMonth", label: "Mês passado" },
];

const resolvePeriod = (id) => {
  const now = new Date();
  switch (id) {
    case "last7":
      return { from: addDays(now, -7), to: addDays(now, 1) };
    case "last30":
      return { from: addDays(now, -30), to: addDays(now, 1) };
    case "previousMonth": {
      const prev = subMonths(now, 1);
      return { from: startOfMonth(prev), to: addDays(endOfMonth(prev), 1) };
    }
    case "currentMonth":
    default:
      return { from: startOfMonth(now), to: addDays(now, 1) };
  }
};

const formatUsd = (value) => {
  const n = Number(value) || 0;
  // Casas decimais adaptativas: contas pequenas mostram precisão fina,
  // valores >= $1 ficam limpos em 2 casas.
  const fractionDigits = n >= 1 ? 2 : 4;
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
};

const formatNumber = (value) => (Number(value) || 0).toLocaleString("pt-BR");

const formatPercent = (value) => {
  if (value === null || value === undefined) return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}%`;
};

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const modalBorder = isDark
    ? "1px solid rgba(255, 255, 255, 0.08)"
    : "1px solid rgba(15, 23, 42, 0.08)";
  const dividerBorder = isDark
    ? "1px solid rgba(255, 255, 255, 0.06)"
    : "1px solid rgba(15, 23, 42, 0.08)";
  const textPrimary = isDark ? "#f8fafc" : "#111827";
  const textMuted = isDark ? "rgba(248, 250, 252, 0.6)" : "#6b7280";
  const surfaceSoft = isDark ? "rgba(255, 255, 255, 0.03)" : "rgba(248, 250, 252, 0.9)";

  return {
    modal: {
      "& .MuiDialog-paper": {
        fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
        width: "min(960px, calc(100vw - 32px))",
        maxWidth: "calc(100vw - 32px)",
        // SEM maxHeight customizado: MUI v4 paperScrollPaper já aplica
        // max-height: calc(100% - 64px) + display: flex; flex-direction:
        // column. Sobrescrever com min(92vh, 860px) quebrava o cálculo de
        // altura do filho .content e o scroll não disparava.
        borderRadius: 24,
        overflow: "hidden",
        background: isDark
          ? "linear-gradient(180deg, #171a20 0%, #111319 100%)"
          : "#ffffff",
        boxShadow: isDark
          ? "0 24px 80px rgba(0, 0, 0, 0.45)"
          : "0 24px 60px rgba(15, 23, 42, 0.12)",
        border: modalBorder,
        "& *": {
          fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
        },
      },
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: theme.spacing(2.5, 3),
      borderBottom: dividerBorder,
      flexShrink: 0,
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
      minWidth: 0,
    },
    headerIcon: {
      width: 40,
      height: 40,
      borderRadius: 12,
      background: "rgba(34, 197, 94, 0.12)",
      color: "#22c55e",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    headerText: { display: "flex", flexDirection: "column", gap: 2, minWidth: 0 },
    title: {
      fontSize: "1.35rem",
      fontWeight: 700,
      color: textPrimary,
      lineHeight: 1.2,
    },
    subtitle: {
      fontSize: "0.82rem",
      color: textMuted,
    },
    closeBtn: {
      color: isDark ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
    },
    content: {
      // Padrão idiomático para flex item rolar dentro de pai flex column
      // (paperScrollPaper). minHeight:0 é o detalhe crítico — sem ele o
      // browser respeita a altura "natural" do filho e ignora overflow:auto.
      padding: theme.spacing(3),
      flex: "1 1 auto",
      minHeight: 0,
      overflowY: "auto",
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2.5),
      "&::-webkit-scrollbar": {
        width: 10,
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: isDark
          ? "rgba(255, 255, 255, 0.18)"
          : "rgba(15, 23, 42, 0.22)",
        borderRadius: 5,
        "&:hover": {
          backgroundColor: isDark
            ? "rgba(255, 255, 255, 0.28)"
            : "rgba(15, 23, 42, 0.32)",
        },
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "transparent",
      },
    },
    toolbar: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      flexWrap: "wrap",
    },
    presetBtn: {
      padding: "6px 12px",
      fontSize: 12,
      fontWeight: 500,
      borderRadius: 8,
      border: modalBorder,
      background: "transparent",
      color: textPrimary,
      cursor: "pointer",
      transition: "all 120ms",
      "&:hover:not(:disabled)": {
        background: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
      },
      "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
    },
    presetBtnActive: {
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      borderColor: "transparent",
      color: "#fff",
      "&:hover:not(:disabled)": {
        background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
      },
    },
    refreshBtn: {
      marginLeft: "auto",
      padding: 6,
      borderRadius: 8,
      border: modalBorder,
      background: "transparent",
      color: textMuted,
      cursor: "pointer",
      "&:hover": { color: textPrimary, background: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)" },
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
    },
    metricsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: theme.spacing(2),
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "1fr",
      },
    },
    metricCard: {
      background: surfaceSoft,
      border: modalBorder,
      borderRadius: 14,
      padding: theme.spacing(2),
      display: "flex",
      flexDirection: "column",
      gap: 4,
    },
    metricLabel: {
      fontSize: "0.7rem",
      fontWeight: 600,
      color: textMuted,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    },
    metricValue: {
      fontSize: "1.9rem",
      fontWeight: 700,
      color: textPrimary,
      lineHeight: 1.1,
    },
    metricDelta: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      fontSize: 12,
      fontWeight: 500,
      marginTop: 4,
    },
    deltaUp: { color: "#ef4444" },
    deltaDown: { color: "#22c55e" },
    deltaNeutral: { color: textMuted },
    chartWrapper: {
      background: surfaceSoft,
      border: modalBorder,
      borderRadius: 14,
      padding: theme.spacing(2, 2.5, 1),
    },
    chartHeader: {
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      marginBottom: theme.spacing(1.5),
    },
    chartTitle: {
      fontSize: 13,
      fontWeight: 700,
      color: textPrimary,
    },
    chartSubtitle: {
      fontSize: 11,
      color: textMuted,
    },
    chartTooltip: {
      background: isDark ? "#1c1f22" : "#ffffff",
      border: modalBorder,
      borderRadius: 10,
      padding: "10px 12px",
      boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
      minWidth: 180,
    },
    chartTooltipTitle: {
      fontSize: 12,
      fontWeight: 700,
      color: textPrimary,
      marginBottom: 6,
    },
    chartTooltipRow: {
      display: "flex",
      justifyContent: "space-between",
      gap: 12,
      fontSize: 11,
      color: textPrimary,
      padding: "2px 0",
    },
    breakdownGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: theme.spacing(2),
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "1fr",
      },
    },
    breakdownTable: {
      background: surfaceSoft,
      border: modalBorder,
      borderRadius: 14,
      overflow: "hidden",
    },
    breakdownHeader: {
      padding: theme.spacing(1.5, 2),
      fontSize: 13,
      fontWeight: 700,
      color: textPrimary,
      borderBottom: dividerBorder,
    },
    breakdownRow: {
      display: "grid",
      gridTemplateColumns: "1fr auto auto",
      gap: theme.spacing(1.5),
      alignItems: "center",
      padding: theme.spacing(1, 2),
      borderBottom: dividerBorder,
      fontSize: 12,
      color: textPrimary,
      "&:last-child": { borderBottom: 0 },
    },
    breakdownLabel: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    breakdownTokens: {
      color: textMuted,
      fontSize: 11,
    },
    breakdownCost: {
      fontWeight: 600,
    },
    recentTable: {
      background: surfaceSoft,
      border: modalBorder,
      borderRadius: 14,
      overflow: "hidden",
    },
    recentHeader: {
      padding: theme.spacing(1.5, 2),
      fontSize: 13,
      fontWeight: 700,
      color: textPrimary,
      borderBottom: dividerBorder,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    recentHeaderHint: {
      fontSize: 11,
      color: textMuted,
      fontWeight: 400,
    },
    recentRow: {
      display: "grid",
      gridTemplateColumns: "1fr 1.4fr 0.9fr 0.9fr 0.9fr 60px",
      gap: theme.spacing(1.5),
      alignItems: "center",
      padding: theme.spacing(1, 2),
      borderBottom: dividerBorder,
      fontSize: 11.5,
      color: textPrimary,
      "&:last-child": { borderBottom: 0 },
    },
    recentRowHeader: {
      color: textMuted,
      fontWeight: 600,
      fontSize: 10.5,
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      background: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)",
    },
    recentTicketLink: {
      color: "#4f7cff",
      textDecoration: "none",
      fontWeight: 500,
      "&:hover": { textDecoration: "underline" },
    },
    empty: {
      padding: theme.spacing(6, 2),
      textAlign: "center",
      color: textMuted,
      fontSize: 13,
    },
    loading: {
      padding: theme.spacing(8, 2),
      display: "flex",
      justifyContent: "center",
    },
  };
});

const OpenAiUsageModal = ({ open, onClose, integration }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === "dark";

  const [period, setPeriod] = useState("currentMonth");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchUsage = useCallback(
    async (opts = {}) => {
      const { from, to } = resolvePeriod(period);
      setLoading(true);
      try {
        const params = {
          from: from.toISOString(),
          to: to.toISOString(),
        };
        if (opts.refresh) params.refresh = "true";
        const { data: result } = await api.get("/integrations/openai/usage", { params });
        setData(result);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    },
    [period]
  );

  useEffect(() => {
    if (!open) return;
    fetchUsage();
  }, [open, fetchUsage]);

  const chartData = useMemo(() => {
    if (!data?.timeline) return [];
    return data.timeline.map((t) => ({
      date: t.date,
      costUsd: Number(t.costUsd) || 0,
      tokens: (Number(t.tokensIn) || 0) + (Number(t.tokensOut) || 0),
      requests: Number(t.requestCount) || 0,
    }));
  }, [data]);

  const grid = isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9";
  const axis = isDark ? "rgba(255,255,255,0.4)" : "#94a3b8";

  const renderDelta = (absValue, percent, invertColors = false) => {
    if (percent === null || percent === undefined) {
      return <span className={`${classes.metricDelta} ${classes.deltaNeutral}`}>— vs anterior</span>;
    }
    if (Math.abs(percent) < 0.05) {
      return (
        <span className={`${classes.metricDelta} ${classes.deltaNeutral}`}>
          0% vs anterior
        </span>
      );
    }
    const isUp = percent > 0;
    // Pra custos, "subir" é ruim (vermelho). invertColors permite reusar a
    // mesma função se um dia trouxermos métricas onde subir é bom.
    const goodWhenUp = invertColors;
    const klass =
      (isUp && goodWhenUp) || (!isUp && !goodWhenUp)
        ? classes.deltaDown
        : classes.deltaUp;
    const Icon = isUp ? ArrowUpRight : ArrowDownRight;
    return (
      <span className={`${classes.metricDelta} ${klass}`}>
        <Icon size={13} />
        {formatPercent(percent)} vs anterior
      </span>
    );
  };

  const tooltipContent = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const point = payload[0]?.payload;
    if (!point) return null;
    return (
      <div className={classes.chartTooltip}>
        <div className={classes.chartTooltipTitle}>
          {(() => {
            try {
              return format(parseISO(label), "dd 'de' MMM, yyyy");
            } catch {
              return label;
            }
          })()}
        </div>
        <div className={classes.chartTooltipRow}>
          <span>Custo</span>
          <strong>{formatUsd(point.costUsd)}</strong>
        </div>
        <div className={classes.chartTooltipRow}>
          <span>Tokens</span>
          <strong>{formatNumber(point.tokens)}</strong>
        </div>
        <div className={classes.chartTooltipRow}>
          <span>Chamadas</span>
          <strong>{formatNumber(point.requests)}</strong>
        </div>
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.modal}
      maxWidth={false}
    >
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <div className={classes.headerIcon}>
            <BarChart3 size={20} />
          </div>
          <div className={classes.headerText}>
            <Typography className={classes.title}>Uso da OpenAI</Typography>
            <Typography className={classes.subtitle}>
              {integration?.name || "Integração OpenAI"} · custo calculado por tokens × preço do modelo
            </Typography>
          </div>
        </div>
        <IconButton size="small" className={classes.closeBtn} onClick={onClose}>
          <X size={18} strokeWidth={1.75} />
        </IconButton>
      </div>

      <div className={classes.content}>
        <div className={classes.toolbar}>
          {PERIOD_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              disabled={loading}
              className={`${classes.presetBtn} ${
                period === preset.id ? classes.presetBtnActive : ""
              }`}
              onClick={() => setPeriod(preset.id)}
            >
              {preset.label}
            </button>
          ))}
          <MuiTooltip title="Atualizar (limpa cache)">
            <button
              type="button"
              className={classes.refreshBtn}
              disabled={loading}
              onClick={() => fetchUsage({ refresh: true })}
              aria-label="Atualizar"
            >
              <RefreshCw size={14} />
            </button>
          </MuiTooltip>
        </div>

        {loading && !data && (
          <div className={classes.loading}>
            <CircularProgress size={28} />
          </div>
        )}

        {data && (
          <>
            <div className={classes.metricsGrid}>
              <div className={classes.metricCard}>
                <span className={classes.metricLabel}>Custo</span>
                <span className={classes.metricValue}>{formatUsd(data.totals.costUsd)}</span>
                {renderDelta(data.delta.costUsdAbs, data.delta.costUsdPercent)}
              </div>
              <div className={classes.metricCard}>
                <span className={classes.metricLabel}>Tokens</span>
                <span className={classes.metricValue}>
                  {formatNumber(data.totals.tokensIn + data.totals.tokensOut)}
                </span>
                {renderDelta(data.delta.tokensTotalAbs, data.delta.tokensTotalPercent)}
              </div>
              <div className={classes.metricCard}>
                <span className={classes.metricLabel}>Chamadas</span>
                <span className={classes.metricValue}>{formatNumber(data.totals.requestCount)}</span>
                {renderDelta(data.delta.requestCountAbs, data.delta.requestCountPercent)}
              </div>
            </div>

            <div className={classes.chartWrapper}>
              <div className={classes.chartHeader}>
                <span className={classes.chartTitle}>Custo diário</span>
                <span className={classes.chartSubtitle}>USD por dia</span>
              </div>
              <div style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="grad-cost" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#22c55e" stopOpacity={0.55} />
                        <stop offset="100%" stopColor="#22c55e" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(v) => {
                        try { return format(parseISO(v), "dd/MM"); } catch { return v; }
                      }}
                      tick={{ fill: axis, fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      interval="preserveStartEnd"
                      minTickGap={20}
                    />
                    <YAxis
                      tick={{ fill: axis, fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      width={50}
                      tickFormatter={(v) => `$${Number(v).toFixed(v >= 1 ? 0 : 2)}`}
                    />
                    <Tooltip content={tooltipContent} cursor={{ stroke: axis, strokeOpacity: 0.2 }} />
                    <Area
                      type="monotone"
                      dataKey="costUsd"
                      stroke="#22c55e"
                      strokeWidth={1.8}
                      fill="url(#grad-cost)"
                      isAnimationActive
                      animationDuration={500}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className={classes.breakdownGrid}>
              <div className={classes.breakdownTable}>
                <div className={classes.breakdownHeader}>Por feature</div>
                {data.byFeature.length === 0 && (
                  <div className={classes.empty}>Sem dados</div>
                )}
                {data.byFeature.map((row) => (
                  <div key={row.key} className={classes.breakdownRow}>
                    <span className={classes.breakdownLabel}>{row.label}</span>
                    <span className={classes.breakdownTokens}>
                      {formatNumber(row.tokensIn + row.tokensOut)} tk · {formatNumber(row.requestCount)} ch
                    </span>
                    <span className={classes.breakdownCost}>{formatUsd(row.costUsd)}</span>
                  </div>
                ))}
              </div>
              <div className={classes.breakdownTable}>
                <div className={classes.breakdownHeader}>Por modelo</div>
                {data.byModel.length === 0 && (
                  <div className={classes.empty}>Sem dados</div>
                )}
                {data.byModel.map((row) => (
                  <div key={row.key} className={classes.breakdownRow}>
                    <span className={classes.breakdownLabel}>{row.label}</span>
                    <span className={classes.breakdownTokens}>
                      {formatNumber(row.tokensIn + row.tokensOut)} tk · {formatNumber(row.requestCount)} ch
                    </span>
                    <span className={classes.breakdownCost}>{formatUsd(row.costUsd)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className={classes.recentTable}>
              <div className={classes.recentHeader}>
                Últimas chamadas
                <span className={classes.recentHeaderHint}>até 50 mais recentes</span>
              </div>
              {data.recentCalls.length === 0 ? (
                <div className={classes.empty}>Sem chamadas no período.</div>
              ) : (
                <>
                  <div className={`${classes.recentRow} ${classes.recentRowHeader}`}>
                    <span>Data</span>
                    <span>Feature</span>
                    <span>Modelo</span>
                    <span>Tokens</span>
                    <span>Custo</span>
                    <span>Ticket</span>
                  </div>
                  {data.recentCalls.map((call) => (
                    <div key={call.id} className={classes.recentRow}>
                      <span>{(() => {
                        try { return format(parseISO(call.createdAt), "dd/MM HH:mm"); }
                        catch { return call.createdAt; }
                      })()}</span>
                      <span>{call.featureLabel}</span>
                      <span>{call.model}</span>
                      <span className={classes.breakdownTokens}>
                        {formatNumber(call.tokensIn)} / {formatNumber(call.tokensOut)}
                      </span>
                      <span className={classes.breakdownCost}>{formatUsd(call.costUsd)}</span>
                      <span>
                        {call.ticketId ? (
                          <a
                            className={classes.recentTicketLink}
                            href={`/tickets/${call.ticketId}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            #{call.ticketId}
                          </a>
                        ) : (
                          <span className={classes.breakdownTokens}>—</span>
                        )}
                      </span>
                    </div>
                  ))}
                </>
              )}
            </div>
          </>
        )}

        {!loading && data && data.totals.requestCount === 0 && (
          <div className={classes.empty}>
            <Sparkles size={28} style={{ opacity: 0.4, marginBottom: 8 }} />
            <div style={{ fontWeight: 500, marginBottom: 4 }}>
              Nenhum uso registrado no período
            </div>
            <div style={{ fontSize: 12 }}>
              Quando a empresa usar features de IA, o consumo aparece aqui.
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default OpenAiUsageModal;
